/**
 * tzUtils.js — 時區工具（搭配 tz-lookup 使用）
 *
 * tz-lookup 回傳 IANA 時區 ID（地理邊界查找），絕大多數國家準確。
 * 但 IANA 資料庫中有兩個條目因「歷史/政治」原因與當前法定時區不符，
 * 需要在程式層面手動覆蓋：
 *
 *   Asia/Urumqi  → UTC+6  （IANA 保留新疆舊時區）
 *   Asia/Kashgar → UTC+6  （同上，為 Asia/Urumqi 的 Link）
 *
 * 中華人民共和國 1949 年起全境統一使用 UTC+8（北京時間），
 * 上述兩個條目在中國行政上均應視為 Asia/Shanghai（UTC+8）。
 * IANA 官方備註亦明確說明：
 *   "Consider using Asia/Shanghai for Beijing Time if that is preferred."
 *
 * 全球其他 340+ 個 IANA 時區均反映當前法定時區，無需覆蓋。
 * 若日後發現新的政治覆蓋案例，只需在 POLITICAL_TZ_OVERRIDE 中新增即可。
 */

/**
 * 需要政治覆蓋的時區對應表。
 * key: tz-lookup 可能回傳的 IANA ID
 * value: 應使用的法定時區 IANA ID
 */
const POLITICAL_TZ_OVERRIDE = {
  // 中國新疆：IANA 地理邊界回傳 UTC+6，法定為 UTC+8（北京時間）
  "Asia/Urumqi": "Asia/Shanghai",
  "Asia/Kashgar": "Asia/Shanghai",
};

/**
 * 將 tz-lookup 回傳的 IANA 時區 ID 解析為法定時區的 UTC 偏移小時數。
 * 支援半小時時區（如印度 +5.5）、45分鐘時區（如尼泊爾 +5.75）。
 *
 * @param {string} tzName - tz-lookup 回傳的 IANA 時區 ID
 * @returns {number} UTC 偏移小時（可為小數，例如 5.5 代表 UTC+5:30）
 */
export function getTzOffset(tzName) {
  const resolvedTz = POLITICAL_TZ_OVERRIDE[tzName] || tzName;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: resolvedTz,
    timeZoneName: "shortOffset",
  });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find((p) => p.type === "timeZoneName");
  if (!tzPart) return 0;

  // tzPart.value 例如 "GMT+8"、"GMT-5"、"GMT+5:30"、"UTC"
  const match = tzPart.value.match(/GMT([+-])(\d+)(?::(\d+))?/);
  if (!match) return 0;

  const sign = match[1] === "+" ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] || "0", 10);
  return sign * (hours + minutes / 60);
}

/**
 * 將 UTC 偏移小時數格式化為顯示字串。
 * @param {number} offset - 例如 8、5.5、-5
 * @returns {string} 例如 "UTC+08:00"、"UTC+05:30"、"UTC-05:00"
 */
export function formatTzOffset(offset) {
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `UTC${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
